import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  createPopup,
  getAllPopup,
  getPopupByID,
  uploadPopupImage,
  updatePopup,
} from "./services/popup.services";
import { clearPopupDetails } from "./popupSlice";
import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";
import { getFile } from "../../_helper/helper";
import FormDatePicker from "../../components/formFields/FormDateField";
import moment from "moment";

const CreatePopup: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const PopupID = useAppSelector((state) => state.drawer.data);
  const PopupDetail = useAppSelector((state) => state.popup.PopupDetail);
  const loading = useAppSelector((state) => state.popup.formLoading);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [PopupDetail]);

  useEffect(() => {
    if (PopupID) {
      dispatch(getPopupByID(PopupID));
    }
    return () => {
      dispatch(clearPopupDetails());
    };
  }, [PopupID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values?.Title ? values?.Title : "",
      URL: values?.URL ? values?.URL : "",
      StartDate: values?.StartDate
        ? moment(values?.StartDate).format("YYYY-MM-DD")
        : null,
      EndDate: values?.EndDate
        ? moment(values?.EndDate).format("YYYY-MM-DD")
        : null,
      IsActive: values?.IsActive === 1,
    };
    const ImageData = new FormData();
    const ImageUpload =
      values?.ImageUpload && typeof values?.ImageUpload !== "string"
        ? getFile(values?.ImageUpload)
        : "";

    try {
      if (PopupID) {
        const editPayload = {
          ...formData,
          ID: PopupID,
        };

        const response = await dispatch(updatePopup(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (values?.ImageUpload && typeof values?.ImageUpload !== "string") {
            ImageData.append("ImageUpload", ImageUpload);
            ImageData.append("ID", PopupID.toString());
            await dispatch(uploadPopupImage(ImageData));
          }
          dispatch(closed());
          dispatch(getAllPopup());
          dispatch(clearPopupDetails());
        }
      } else {
        const response = await dispatch(createPopup(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.ImageUpload &&
            typeof values?.ImageUpload !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("ImageUpload", ImageUpload);
            ImageData.append("ID", response?.payload?.Data?.ID.toString());
            await dispatch(uploadPopupImage(ImageData));
          }
          dispatch(closed());
          dispatch(getAllPopup());
          dispatch(clearPopupDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleStartDateChange = (e: any, formRenderProps: FormRenderProps) => {
    const newStartDate = e.value;
    formRenderProps.onChange("StartDate", { value: newStartDate });

    const endDate = formRenderProps.valueGetter("EndDate");
    if (
      endDate &&
      newStartDate &&
      moment(endDate).isBefore(moment(newStartDate), "day")
    ) {
      formRenderProps.onChange("EndDate", { value: newStartDate });
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={PopupDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
                <GridLayoutItem className="master-drawer-header">
                  <Typography.h4 className="m-0">
                    {PopupID ? "Update Popup" : "Add Popup"}
                  </Typography.h4>

                  <IconButton
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                    icon={RxCross1}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Title"
                    label="Title"
                    placeholder="Title"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="URL"
                    label="URL"
                    placeholder="URL"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperStyle={{ width: "100%" }}
                    name="StartDate"
                    label="Start Date"
                    format="dd/MM/yyyy"
                    min={moment().toDate()}
                    component={FormDatePicker}
                    validator={requiredValidator}
                    astrike={true}
                    onChange={(e: any) =>
                      handleStartDateChange(e, formRenderProps)
                    }
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperStyle={{ width: "100%" }}
                    name="EndDate"
                    label="End Date"
                    format="dd/MM/yyyy"
                    min={formRenderProps.valueGetter("StartDate")}
                    component={FormDatePicker}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    justifyContent="start"
                    name={`ImageUpload`}
                    label="Image"
                    type="file"
                    alternateName="Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`IsActive`}
                    label="Status"
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: "20px",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={PopupID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreatePopup;
